import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Upload, Trash2, Edit, FileText, Plus, Info } from 'lucide-react';
import { Tooltip } from '../../../../../components/ui/tooltip';
import type { DashboardProject } from '../../../types/project';
import { DocumentUploadModal } from '../production/DocumentUploadModal';
import { DocumentDownloadModal } from '../production/DocumentDownloadModal';
import { bulkDeleteInspectionPersonnel, deleteInspectionPersonnelDocumentsByDocId } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { AddQCInspectionPersonnelModal } from './AddQCInspectionPersonnelModal';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { formatDateOnly } from '../../../../../lib/formatUtils';
import { fetchInspectionPersonnelData, refreshQCInspectionPersonnelData } from '../../../../../redux/Project/registryData/action';

interface QCInspectionPersonnelTableProps {
  project: DashboardProject;
  onRefresh?: () => void;
}

export const QCInspectionPersonnelTable: React.FC<QCInspectionPersonnelTableProps> = ({ project, onRefresh }) => {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;
  
  // Get QC Inspection Personnel data from Redux store
  const registryData = useSelector((state: any) => state.registryData);
  const inspectionPersonnelDataFromStore = useMemo(() => {
    return projectId ? registryData?.inspectionPersonnelData?.[projectId] : null;
  }, [registryData?.inspectionPersonnelData, projectId]);
  const inspectionPersonnelLoading = registryData?.inspectionPersonnelLoading || false;

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<any>(null);
  const [personnelToEdit, setPersonnelToEdit] = useState<any>(null);
  const [personnelToDelete, setPersonnelToDelete] = useState<any>(null);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [personnelList, setPersonnelList] = useState<any[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Process personnel data from store
  const processPersonnelData = (data: any) => {
    if (!data) return [];
    
    let personnel: any[] = [];
    
    if (Array.isArray(data)) {
      personnel = data;
    } else if (typeof data === 'object' && data !== null) {
      Object.values(data).forEach((userGroup: any) => {
        if (userGroup && userGroup.personnel && Array.isArray(userGroup.personnel)) {
          personnel.push(...userGroup.personnel);
        }
      });
    }
    
    return personnel;
  };

  // Fetch QC Inspection Personnel data from Redux store or dispatch action
  useEffect(() => {
    if (!projectId) return;

    if (inspectionPersonnelDataFromStore) {
      const personnel = processPersonnelData(inspectionPersonnelDataFromStore);
      setPersonnelList(personnel);
    } else if (!inspectionPersonnelLoading) {
      // Fetch from API if not in store and not currently loading
      dispatch(fetchInspectionPersonnelData({ projectId }));
    }
  }, [inspectionPersonnelDataFromStore, projectId, inspectionPersonnelLoading, dispatch]);

  // Update personnel list when store data changes
  useEffect(() => {
    if (inspectionPersonnelDataFromStore) {
      const personnel = processPersonnelData(inspectionPersonnelDataFromStore);
      setPersonnelList(personnel);
    }
  }, [inspectionPersonnelDataFromStore]);

  const handleUpload = (personnel: any) => {
    setSelectedPersonnel(personnel);
    setIsUploadModalOpen(true);
  };

  const handleDocumentClick = (personnel: any) => {
    setSelectedPersonnel(personnel);
    setIsDownloadModalOpen(true);
  };

  const handleEdit = (personnel: any) => {
    setPersonnelToEdit(personnel);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (personnel: any) => {
    setPersonnelToDelete(personnel);
    setIsDeleteModalVisible(true);
  };

  const handleUploadComplete = async (response: any) => {
    if (response && selectedPersonnel) {
      // Refresh Redux store data
      if (projectId) {
        dispatch(refreshQCInspectionPersonnelData({ projectId }));
      }
      if (onRefresh) {
        onRefresh();
      }
      setIsUploadModalOpen(false);
      setSelectedPersonnel(null);
    }
  };

  const handleDocumentDownload = (doc: any) => {
    if (doc && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    if (!personnelToDelete || !personnelToDelete.id) {
      console.error('Personnel ID is missing');
      return;
    }

    try {
      const payload = {
        ids: [personnelToDelete.id]
      };
      const result = await bulkDeleteInspectionPersonnel(payload);
      if (result) {
        // Refresh Redux store data
      if (projectId) {
        dispatch(refreshQCInspectionPersonnelData({ projectId }));
      }
        if (onRefresh) {
          onRefresh();
        }
        setIsDeleteModalVisible(false);
        setPersonnelToDelete(null);
      }
    } catch (error) {
      console.error('Failed to delete personnel:', error);
    }
  };

  const handleEditComplete = () => {
    setIsEditModalOpen(false);
    setPersonnelToEdit(null);
    // Refresh Redux store data
    if (projectId) {
      dispatch(refreshQCInspectionPersonnelData({ projectId }));
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle document deletion
  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDocumentDelete = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDocumentDelete = async () => {
    if (!documentToDelete || !selectedPersonnel) return;

    setDeleteDocumentLoading(true);
    try {
      const docId = documentToDelete.docId || documentToDelete.doc_id || documentToDelete.id;
      
      if (!docId) {
        customNotification.error('Failed to delete document', 'Document ID not found');
        setDeleteDocumentLoading(false);
        return;
      }

      const apiPayload = {
        docIds: [Number(docId)]
      };

      const deleteResponse = await deleteInspectionPersonnelDocumentsByDocId(apiPayload);

      if (deleteResponse) {
        // Refresh Redux store data
      if (projectId) {
        dispatch(refreshQCInspectionPersonnelData({ projectId }));
      }
        if (onRefresh) {
          onRefresh();
        }
        setIsDocumentDeleteModalOpen(false);
        setIsDownloadModalOpen(false);
        setSelectedPersonnel(null);
        setDocumentToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      customNotification.error('Failed to delete document', 'An error occurred while deleting the document.');
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  // Helper function to extract documents from nested structure
  const extractDocuments = (documents: any[]): any[] => {
    if (!documents || documents.length === 0) return [];
    
    if (documents[0]?.materials && Array.isArray(documents[0].materials)) {
      return documents.flatMap((docGroup: any) => 
        (docGroup.materials || []).map((doc: any) => ({
          ...doc,
          docRef: docGroup.docRef
        }))
      );
    }
    
    return documents;
  };

  const transformDocumentsForDownload = (documents: any[]) => {
    if (!documents || documents.length === 0) return [];
    
    const flatDocuments = extractDocuments(documents);
    
    return flatDocuments.map((doc: any) => ({
      id: doc.registryDocId || doc.id || doc.docId || Math.random().toString(),
      fileName: doc.fileName || doc.file_name,
      fileUrl: doc.fileUrl || doc.file_url,
      uploadDate: doc.uploadDate || doc.uploadedAt || doc.createdAt,
      uploadedBy: doc.uploadedBy || doc.createdBy,
      docId: doc.docId || doc.doc_id || doc.id
    }));
  };

  if (inspectionPersonnelLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-[13px] text-heritage-grey">Loading QC Inspection Personnel...</div>
      </div>
    );
  }

  if (personnelList.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">👤</span>
          </div>
          <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">No personnel found</h3>
          <p className="text-[13px] text-heritage-grey max-w-sm">
            No QC Inspection Personnel have been added yet. Click "Add New Personnel" to get started.
          </p>
        </div>

        <AddQCInspectionPersonnelModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleEditComplete}
          project={project}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                  <TableHead className="min-w-[180px]">Operator Name</TableHead>
                  <TableHead className="text-center">Certification Number</TableHead>
                  <TableHead className="text-center">Eyesight Examination Passed</TableHead>
                  <TableHead className="text-center">Eyesight Examination Expiry Date</TableHead>
                  <TableHead>Qualifications</TableHead>
                  <TableHead className="text-center w-[80px]">Documents</TableHead>
                  <TableHead className="text-center w-[80px] sticky right-0 z-10 bg-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {personnelList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[13px] text-heritage-grey py-8">
                      No personnel found.
                    </TableCell>
                  </TableRow>
                ) : (
                  personnelList.map((personnel, index) => {
                    const extractedDocs = extractDocuments(personnel.documents || []);
                    const docCount = extractedDocs.length;
                    const qualifications = personnel.qualifications || [];

                    return (
                      <TableRow key={personnel.id || index}>
                        <TableCell className="min-w-[180px] whitespace-nowrap">{personnel.operator_name || '-'}</TableCell>
                        <TableCell className="text-center">{personnel.certification_number || '-'}</TableCell>
                        <TableCell className="text-center">
                          {personnel.eyesight_examination_passed !== undefined
                            ? (personnel.eyesight_examination_passed ? 'Yes' : 'No')
                            : '-'
                          }
                        </TableCell>
                        <TableCell className="text-center">
                          {formatDateOnly(personnel.eyesight_examination_expiry_date) || '-'}
                        </TableCell>
                        <TableCell>
                          {qualifications.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {qualifications.map((qual: any, idx: number) => {
                                const expiryDate = qual.expiration_date ? formatDateOnly(qual.expiration_date) : null;
                                const isExpired = qual.expiration_date && new Date(qual.expiration_date) < new Date();
                                return (
                                  <Tooltip
                                    key={idx}
                                    placement="bottom"
                                    content={
                                      <div className="text-left space-y-1 text-[11px]">
                                        <div><span className="font-semibold">Level:</span> {qual.level_type || '-'}</div>
                                        <div><span className="font-semibold">Cert #:</span> {qual.certification_number || qual.qualification_id || '-'}</div>
                                        {isExpired && <div className="font-semibold opacity-80">Expired</div>}
                                      </div>
                                    }
                                  >
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blueprint-blue bg-[rgba(0,39,63,0.06)] px-2 py-1 rounded-full cursor-default">
                                      {qual.qualification_type || 'Unknown'}
                                      {expiryDate && (
                                        <>
                                          {' · '}
                                          <span className={isExpired ? 'line-through text-heritage-grey' : ''}>{expiryDate}</span>
                                        </>
                                      )}
                                      <Info className="h-3 w-3 opacity-60" />
                                    </span>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            {docCount > 0 ? (
                              <Button
                                className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                title="View Documents"
                                variant="outline"
                                onClick={() => handleDocumentClick(personnel)}
                              >
                                <FileText className="h-4 w-4" />
                                {docCount}
                              </Button>
                            ) : (
                              <span className="text-[13px] text-heritage-grey">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center w-[80px] sticky right-0 z-10 bg-white">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              onClick={() => handleEdit(personnel)}
                              className="text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                              title="Edit Personnel"
                              variant="outline"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              className="text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => handleDeleteClick(personnel)}
                              title="Delete Personnel"
                              variant="outline"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      {selectedPersonnel && isUploadModalOpen && (
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSelectedPersonnel(null);
          }}
          onUpload={handleUploadComplete}
          title="Upload QC Inspection Personnel Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={100}
          buttonText="Select Document"
          label="Document"
          description="Upload QC Inspection Personnel documents (PDF, Excel, DOC, etc...)"
          documentType="QC_INSPECTION_PERSONNEL"
        />
      )}

      {/* Download Modal */}
      {selectedPersonnel && selectedPersonnel.documents && extractDocuments(selectedPersonnel.documents).length > 0 && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setSelectedPersonnel(null);
          }}
          documents={transformDocumentsForDownload(selectedPersonnel.documents)}
          onDownload={handleDocumentDownload}
          onDelete={(doc) => handleDeleteDocument(doc)}
          title="Download QC Inspection Personnel Document"
          canDelete={true}
        />
      )}

      {/* Edit Modal */}
      {personnelToEdit && (
        <AddQCInspectionPersonnelModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setPersonnelToEdit(null);
          }}
          onAdd={handleEditComplete}
          project={project}
          personnelToEdit={personnelToEdit}
          existingPersonnel={personnelList}
        />
      )}

      {/* Add Modal */}
      <AddQCInspectionPersonnelModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleEditComplete}
        project={project}
        existingPersonnel={personnelList}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDeleteModalVisible}
        onClose={() => {
          setIsDeleteModalVisible(false);
          setPersonnelToDelete(null);
        }}
        onConfirm={handleDelete}
        itemName={personnelToDelete ? (personnelToDelete.operator_name || '') : ''}
        itemType="personnel"
      />

      {/* Document Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDocumentDeleteModalOpen}
        onClose={handleCancelDocumentDelete}
        onConfirm={handleConfirmDocumentDelete}
        isLoading={deleteDocumentLoading}
        itemToDelete={documentToDelete}
        itemType="Personnel Document"
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title="Delete QC Inspection Personnel Document"
      />
    </>
  );
};

